import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api, { fileUrl } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useSync } from '../context/SyncContext';
import { deleteQueueItem, getQueue, cacheGet, cacheSet } from '../lib/db';
import Breadcrumbs from '../components/Breadcrumbs';
import InspectionBanner from '../components/InspectionBanner';
import CameraCapture from '../components/CameraCapture';
import ReportBuilderDialog from '../components/ReportBuilderDialog';
import { Dialog, DialogContent, DialogTitle } from '../components/ui/dialog';
import { POSITIONS } from '../lib/status';
import { Building2, Layers, Loader2, Camera, Trash2, PackagePlus, Wrench, Check, ClipboardList, RotateCcw, Eye, ChevronLeft, ChevronRight, Pencil, ArrowLeft, ArrowRight, Image as ImageIcon, DoorOpen, DoorClosed, Lock } from 'lucide-react';
import { toast } from 'sonner';

const EDIT_STATUSES = [
  { v: 'installed', l: 'Installed' }, { v: 'inspected_ok', l: 'Inspected OK' },
  { v: 'damaged', l: 'Damaged' }, { v: 'repair_pending', l: 'Repair Pending' },
  { v: 'repaired', l: 'Repaired' }, { v: 'replaced', l: 'Replaced' }, { v: 'not_installed', l: 'Not Installed' },
];

const POS_ORDER = ['poster_left', 'poster_right', 'door_vinyl', 'door_side'];
const isPoster = (t) => t === 'poster_left' || t === 'poster_right';
const wallOf = (t) => (t === 'poster_left' ? 'Left' : 'Right');
const INSPECT_RESULTS = [
  { key: 'ok', label: 'OK', active: 'border-emerald-500 bg-emerald-600' },
  { key: 'notok', label: 'Not OK (Damaged)', active: 'border-rose-500 bg-rose-600' },
];
const ACTION_LABEL = { deinstall: 'Deinstall', install: 'Install', inspect_ok: 'OK', damage: 'Not OK', repair: 'Repair' };

// This round's evolving stage, derived ONLY from actions captured in this inspection
function roundStage(isInstall, acts, skipDeinstall) {
  const last = acts[acts.length - 1];
  if (isInstall) {
    if (!last) return skipDeinstall
      ? { kind: 'install', label: 'Install', Icon: PackagePlus, cls: 'bg-primary text-primary-foreground hover:opacity-90' }
      : { kind: 'deinstall', label: 'Deinstall', Icon: Trash2, cls: 'bg-secondary text-foreground hover:bg-secondary/70' };
    if (last === 'deinstall') return { kind: 'install', label: 'Install', Icon: PackagePlus, cls: 'bg-primary text-primary-foreground hover:opacity-90' };
    return { kind: 'done', label: 'Done', Icon: Check, cls: 'bg-emerald-600 text-white cursor-default', frozen: true };
  }
  if (!last) return { kind: 'inspect', label: 'Capture', Icon: Camera, cls: 'bg-primary text-primary-foreground hover:opacity-90' };
  if (last === 'inspect_ok') return { kind: 'done', label: 'Done', Icon: Check, cls: 'bg-emerald-600 text-white cursor-default', frozen: true };
  if (last === 'damage') return { kind: 'repair', label: 'Repair', Icon: Wrench, cls: 'bg-cyan-600 text-white hover:bg-cyan-500' };
  if (last === 'repair') return { kind: 'done', label: 'Repaired', Icon: Wrench, cls: 'bg-emerald-600 text-white cursor-default', frozen: true };
  return { kind: 'inspect', label: 'Capture', Icon: Camera, cls: 'bg-primary text-primary-foreground hover:opacity-90' };
}

function statusMeta(isInstall, acts) {
  const last = acts[acts.length - 1];
  const todo = ['TO DO', 'border-slate-400 bg-slate-200 text-slate-800'];
  const action = ['NEEDS ACTION', 'border-rose-500 bg-rose-200 text-rose-900'];
  const done = ['DONE', 'border-emerald-500 bg-emerald-200 text-emerald-900'];
  if (isInstall) {
    if (!last) return todo;
    if (last === 'deinstall') return action;
    return done;
  }
  if (!last) return todo;
  if (last === 'damage') return action;
  if (last === 'inspect_ok' || last === 'repair') return done;
  return todo;
}

// Per-site-type color system (approved): poster left=blue+⬅, poster right=violet+➡, door vinyl=orange, door side=teal
// Rendered as a pill with a solid icon chip — visually distinct from the solid lift block
const SITE_TYPE = {
  poster_left: { label: 'POSTER LEFT WALL', cls: 'border-blue-500 bg-blue-100 text-blue-800', chip: 'bg-blue-600 text-white', Icon: ArrowLeft },
  poster_right: { label: 'POSTER RIGHT WALL', cls: 'border-violet-500 bg-violet-100 text-violet-800', chip: 'bg-violet-600 text-white', Icon: ArrowRight },
  door_vinyl: { label: 'DOOR VINYL', cls: 'border-orange-500 bg-orange-100 text-orange-800', chip: 'bg-orange-500 text-white', Icon: DoorOpen },
  door_side: { label: 'DOOR SIDE', cls: 'border-teal-500 bg-teal-100 text-teal-800', chip: 'bg-teal-600 text-white', Icon: DoorClosed },
};

// Tile styling by state (approved): done=green, action-pending(install/repair)=red, not-started=grey.
function toneStyles(kind) {
  if (kind === 'done') return {
    tile: 'border-emerald-600 bg-emerald-50',
    pillar: 'border-emerald-300 bg-emerald-100 text-emerald-900',
    thumbLabel: 'text-emerald-800',
    outline: 'border-emerald-400 text-emerald-800 hover:bg-emerald-100',
    btn: 'bg-emerald-600 text-white cursor-default',
  };
  if (kind === 'install' || kind === 'repair') return {
    tile: 'border-rose-600 bg-rose-50',
    pillar: 'border-rose-300 bg-rose-100 text-rose-900',
    thumbLabel: 'text-rose-800',
    outline: 'border-rose-400 text-rose-800 hover:bg-rose-100',
    btn: 'bg-rose-600 text-white hover:bg-rose-500',
  };
  return {
    tile: 'border-slate-400 bg-white',
    pillar: 'border-slate-300 bg-slate-100 text-slate-800',
    thumbLabel: 'text-slate-600',
    outline: 'border-border text-slate-600 hover:border-primary hover:text-slate-900',
    btn: null,
  };
}

export default function InspectionView() {
  const { id } = useParams();
  const { user } = useAuth();
  const { enqueueAction, online, queue, refresh, processQueue, retryItem } = useSync();
  const readOnly = ['client', 'society_manager'].includes(user.role);
  const [insp, setInsp] = useState(null);
  const [societies, setSocieties] = useState(null);
  const [serverProofs, setServerProofs] = useState({});
  const [eligMap, setEligMap] = useState({});
  const [campMap, setCampMap] = useState({});
  const [cam, setCam] = useState(null);
  const [busySite, setBusySite] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [selSoc, setSelSoc] = useState(null);
  const [groupBy, setGroupBy] = useState('society');
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ campaign_id: '', new_status: '', notes: '' });
  const [view, setView] = useState('sites');
  const urlCache = useRef({});
  const canEdit = !readOnly && !(insp?.kind === 'deinstall' && user.role !== 'admin');

  const load = useCallback(async () => {
    try {
      const i = (await api.get(`/inspections/${id}`)).data;
      setInsp(i);
      const socIds = (i.society_ids && i.society_ids.length) ? i.society_ids : (i.society_id ? [i.society_id] : []);
      const layouts = (await Promise.all(socIds.map((sid) => api.get(`/societies/${sid}/layout`).then((r) => r.data).catch(() => null)))).filter(Boolean);
      const societiesVal = layouts.map((l) => ({ id: l.society?.id, name: l.society?.name || 'Society', towers: l.towers || [] }));
      setSocieties(societiesVal);
      const proofQ = i.kind === 'deinstall' ? `deinstall_inspection_id=${id}` : `round_inspection_id=${id}`;
      const pr = (await api.get(`/proofs?${proofQ}&limit=1000`)).data.proofs || [];
      const map = {};
      pr.forEach((p) => { (map[p.site_id] = map[p.site_id] || []).push(p); });
      Object.values(map).forEach((arr) => arr.sort((a, b) => ((a.created_at || '') < (b.created_at || '') ? -1 : 1)));
      setServerProofs(map);
      // Once the server has the proof, drop the confirmed synced local copy (prevents timer-race flicker / vanishing thumbnails)
      const srvClientIds = new Set(pr.map((p) => p.client_action_id).filter(Boolean));
      const confirmed = (await getQueue()).filter((it) => it.status === 'synced' && srvClientIds.has(it.id));
      if (confirmed.length) { await Promise.all(confirmed.map((it) => deleteQueueItem(it.id))); refresh(); }
      const scoped = i.site_ids || [];
      const eligs = await Promise.all(scoped.map((sid) => api.get(`/sites/${sid}/eligible-campaigns`).then((r) => [sid, r.data]).catch(() => [sid, []])));
      const eligMapVal = Object.fromEntries(eligs);
      setEligMap(eligMapVal);
      const camps = await api.get('/campaigns').then((r) => r.data).catch(() => []);
      const campMapVal = Object.fromEntries(camps.map((c) => [c.id, c]));
      setCampMap(campMapVal);
      // Cache the whole layout so a cold offline reopen still renders the sites and lets the operator keep capturing.
      cacheSet(`insp:${id}`, { insp: i, societies: societiesVal, serverProofs: map, eligMap: eligMapVal, campMap: campMapVal });
    } catch {
      // Offline / transient network error — keep whatever data is already loaded; captures still queue locally.
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    // Hydrate instantly from the last cached layout so a cold offline reopen still shows the sites.
    (async () => {
      const snap = await cacheGet(`insp:${id}`);
      if (snap) {
        setInsp((cur) => cur || snap.insp);
        setSocieties((cur) => (cur && cur.length ? cur : snap.societies || []));
        setServerProofs((cur) => (cur && Object.keys(cur).length ? cur : snap.serverProofs || {}));
        setEligMap((cur) => (cur && Object.keys(cur).length ? cur : snap.eligMap || {}));
        setCampMap((cur) => (cur && Object.keys(cur).length ? cur : snap.campMap || {}));
      }
    })();
  }, [id]);
  useEffect(() => { if (online) processQueue(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const h = () => load();
    window.addEventListener('sync-updated', h);
    return () => window.removeEventListener('sync-updated', h);
  }, [load]);

  const queueUrl = (it) => {
    if (!urlCache.current[it.id] && it.files?.[0]?.blob) urlCache.current[it.id] = URL.createObjectURL(it.files[0].blob);
    return urlCache.current[it.id] || null;
  };

  // This round's captured entries per site = synced server proofs + not-yet-synced queue items
  const entriesFor = (siteId) => {
    const srv = (serverProofs[siteId] || []).filter((p) => p.file_ids?.length).map((p) => ({
      action_type: p.action_type === 'replace' ? 'deinstall' : p.action_type,
      url: fileUrl(p.file_ids[0]), source: 'server', proofId: p.id,
      campaign_id: p.campaign_id || '', new_status: p.new_status || '', notes: p.notes || '',
    }));
    // Keep a synced queue item visible until the server proof for it has been refetched,
    // so the tile never flickers back to the previous step while sync/load are in flight.
    const syncedClientIds = new Set((serverProofs[siteId] || []).map((p) => p.client_action_id).filter(Boolean));
    const q = queue.filter((it) => it.siteId === siteId && it.payload?.inspection_id === id
      && (it.status !== 'synced' || !syncedClientIds.has(it.id))).map((it) => ({
      action_type: it.payload.action_type, url: queueUrl(it), source: 'queue', queueId: it.id, status: it.status,
      campaign_id: it.payload.campaign_id || '',
    }));
    return [...srv, ...q];
  };

  const inspCampIds = insp ? new Set(insp.campaign_ids || (insp.campaign_id ? [insp.campaign_id] : [])) : new Set();
  const installIds = insp ? new Set(insp.install_campaign_ids || []) : new Set();
  const isInstallSite = (siteId) => installIds.size
    ? (eligMap[siteId] || []).some((c) => installIds.has(c.id))
    : (eligMap[siteId] || []).some((c) => c.is_installation && inspCampIds.has(c.id));

  // The NEW board for this site = the install-marked campaign mapped to it (auto install target)
  const installTarget = (siteId) => {
    const eligs = eligMap[siteId] || [];
    if (installIds.size) return eligs.find((c) => installIds.has(c.id)) || null;
    return eligs.find((c) => c.is_installation && inspCampIds.has(c.id)) || null;
  };

  const scopedCampaigns = (siteId) => {
    const all = eligMap[siteId] || [];
    const today = new Date().toISOString().slice(0, 10);
    const isActive = (c) => c.status !== 'cancelled' && (!c.start_date || String(c.start_date).slice(0, 10) <= today) && (!c.end_date || String(c.end_date).slice(0, 10) >= today);
    const filtered = all.filter((c) => inspCampIds.has(c.id) || isActive(c));
    return filtered.length ? filtered : all;
  };

  const stampFor = (socName, t, l, s) => {
    const label = isPoster(s.position_type) ? `Poster · ${wallOf(s.position_type)} wall` : POSITIONS[s.position_type];
    return [label, `${socName} · ${t.name} · ${l.name}`];
  };

  const openCapture = (socName, t, l, s, kind) => {
    const configured = ((insp.available_campaign_ids && insp.available_campaign_ids.length ? insp.available_campaign_ids : insp.deinstall_campaign_ids) || []).map((cid) => campMap[cid]).filter(Boolean);
    const allCamps = Object.values(campMap);
    const base = configured.length ? configured : (allCamps.length ? allCamps : scopedCampaigns(s.id));
    // Chips = ALL clients from the client master (+ campaign-only client names); each links to its
    // matching campaign when one exists, so captures carry campaign_id when available and client name always.
    const allowedClients = (insp.available_client_ids && insp.available_client_ids.length) ? new Set(insp.available_client_ids) : null;
    const clientChips = (insp.clients || [])
      .filter((cl) => !allowedClients || allowedClients.has(cl.id))
      .map((cl) => {
        const mc = base.find((c) => (cl.id && c.client_user_id === cl.id) || c.client_name === cl.name) || null;
        return { key: `cl-${cl.id}`, label: cl.name, client_user_id: cl.id.startsWith('name:') ? '' : cl.id, client_name: cl.name, campaign_id: mc ? mc.id : '' };
      });
    let chips = clientChips.length ? clientChips : base.map((c) => ({ key: c.id, label: c.client_name || c.name, campaign_id: c.id, client_user_id: c.client_user_id || '', client_name: c.client_name || '' }));
    let initialCampaignId = '';
    // Expected occupant of this wall = latest client tapped this round, else the DB record
    const occCampId = [...entriesFor(s.id)].reverse().find((e) => e.campaign_id)?.campaign_id || s.current_campaign_id || '';
    let expectedCampaignId = '';
    let expectedClientName = '';
    if (kind === 'install') {
      const tgt = installTarget(s.id);
      if (tgt) { chips = [{ key: tgt.id, label: tgt.client_name || tgt.name, campaign_id: tgt.id, client_user_id: tgt.client_user_id || '', client_name: tgt.client_name || '' }]; initialCampaignId = tgt.id; }
    }
    if (kind === 'deinstall') {
      // Preselect the expected old client (replace_map override, else current occupant); operator can still change it
      const tgt = installTarget(s.id);
      const repOldId = (insp.replace_map || {})[tgt?.id] || occCampId || '';
      const match = chips.find((c) => c.campaign_id === repOldId);
      initialCampaignId = match ? match.key : '';
      expectedCampaignId = repOldId;
      expectedClientName = campMap[repOldId] ? (campMap[repOldId].client_name || campMap[repOldId].name) : '';
    }
    if (kind === 'inspect' || kind === 'repair') {
      expectedCampaignId = occCampId;
      expectedClientName = campMap[occCampId] ? (campMap[occCampId].client_name || campMap[occCampId].name) : '';
    }
    const requireCampaign = chips.length > 0;
    const titleMap = { deinstall: 'Deinstall — remove old board', install: 'Install — new board', inspect: 'Inspect site', repair: 'Repair — capture fix' };
    setCam({ siteId: s.id, kind, stampLines: stampFor(socName, t, l, s), title: titleMap[kind], campaignOptions: chips, requireCampaign, results: kind === 'inspect' ? INSPECT_RESULTS : null, initialCampaignId, expectedCampaignId, expectedClientName });
  };

  const onCapture = async (shot) => {
    const c = cam; setCam(null);
    const gps = shot.gps ? { lat: shot.gps.lat, lng: shot.gps.lng } : null;
    let action_type = c.kind;
    if (c.kind === 'inspect') action_type = shot.result === 'notok' ? 'damage' : 'inspect_ok';
    try {
      await enqueueAction({ siteId: c.siteId, action_type, campaign_id: shot.campaignId || null, client_user_id: shot.clientUserId || null, client_name: shot.clientName || null, gps, files: [shot], inspection_id: id });
      toast.success(online ? 'Saved & syncing' : 'Saved locally — will sync when online');
    } catch (e) {
      toast.error('Could not save the photo. Please capture again.');
    }
  };

  const openEdit = (entry) => {
    setEditForm({ campaign_id: entry.campaign_id || '', new_status: entry.new_status || '', notes: entry.notes || '' });
    setEditOpen(true);
  };
  const saveProofEdit = async () => {
    const e = lightbox.entries[lightbox.index];
    if (!e.proofId) return;
    setSavingEdit(true);
    try {
      await api.patch(`/inspections/${id}/sites/${lightbox.siteId}/proofs/${e.proofId}`, {
        campaign_id: editForm.campaign_id || null,
        new_status: editForm.new_status || null,
        notes: editForm.notes || '',
      });
      toast.success('Photo details updated');
      setEditOpen(false);
      setLightbox(null);
      await load();
    } catch { toast.error('Update failed'); }
    finally { setSavingEdit(false); }
  };

  const deleteLast = async (siteId) => {
    const entries = entriesFor(siteId);
    if (!entries.length) return;
    const last = entries[entries.length - 1];
    setBusySite(siteId);
    try {
      if (last.source === 'queue') {
        await deleteQueueItem(last.queueId);
        if (last.status === 'synced') await api.delete(`/inspections/${id}/sites/${siteId}/last-proof`);
        await refresh();
      } else { await api.delete(`/inspections/${id}/sites/${siteId}/last-proof`); }
      toast.success('Last capture removed');
      await load();
    } catch { toast.error('Could not remove the capture'); }
    finally { setBusySite(null); }
  };

  if (!insp || !societies) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  const scoped = new Set(insp.site_ids || []);
  const headTitle = insp.name || (societies.length === 1 ? societies[0].name : `${societies.length} societies`);
  const societyViews = societies.map((soc) => ({
    ...soc,
    visTowers: (soc.towers || [])
      .map((t) => ({ ...t, lifts: (t.lifts || []).map((l) => ({ ...l, sites: l.sites || [] })).filter((l) => l.sites.length) }))
      .filter((t) => t.lifts.length),
  })).filter((soc) => soc.visTowers.length);
  const campChoices = Object.values(Object.fromEntries(Object.values(eligMap).flat().map((c) => [c.id, { id: c.id, name: c.name, client_name: c.client_name }])));

  const socStats = (soc) => {
    let total = 0, done = 0, issues = 0;
    soc.visTowers.forEach((t) => t.lifts.forEach((l) => l.sites.forEach((s) => {
      if (!scoped.has(s.id)) return;
      total++;
      const st = roundStage(isInstallSite(s.id), entriesFor(s.id).map((e) => e.action_type));
      if (st.kind === 'done') done++; else if (st.kind === 'install' || st.kind === 'repair') issues++;
    })));
    return { total, done, issues };
  };
  const activeSoc = societyViews.find((x) => x.id === selSoc) || (societyViews.length === 1 ? societyViews[0] : null);
  const towerStats = (t) => {
    let total = 0, done = 0;
    t.lifts.forEach((l) => l.sites.forEach((s) => {
      if (!scoped.has(s.id)) return;
      total++;
      if (roundStage(isInstallSite(s.id), entriesFor(s.id).map((e) => e.action_type)).kind === 'done') done++;
    }));
    return { total, done };
  };
  const activeStats = activeSoc ? socStats(activeSoc) : null;
  const activePct = activeStats && activeStats.total ? Math.round((activeStats.done / activeStats.total) * 100) : 0;
  const scrollToTower = (tid) => { const el = document.getElementById(`iv-towsec-${tid}`); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  const isAdmin = user.role === 'admin';
  const allSites = societyViews.flatMap((soc) => soc.visTowers.flatMap((t) => t.lifts.flatMap((l) => l.sites.filter((s) => scoped.has(s.id)).map((s) => ({ soc, t, l, s })))));
  const repairSites = allSites.filter((o) => roundStage(isInstallSite(o.s.id), entriesFor(o.s.id).map((e) => e.action_type)).kind === 'repair');
  const repairGroups = Object.entries(repairSites.reduce((m, o) => { (m[o.soc.name] = m[o.soc.name] || []).push(o); return m; }, {}));
  const clientOf = (sid) => { const cs = [...new Set((eligMap[sid] || []).map((c) => c.client_name).filter(Boolean))]; return cs.join(', ') || 'Unassigned'; };
  const groupKey = (o) => {
    if (groupBy === 'client') return clientOf(o.s.id);
    if (groupBy === 'site_type') return isPoster(o.s.position_type) ? 'Poster' : 'Door';
    if (groupBy === 'status') return statusMeta(isInstallSite(o.s.id), entriesFor(o.s.id).map((e) => e.action_type))[0];
    return o.soc.name;
  };
  const groups = {};
  allSites.forEach((o) => { const k = groupKey(o); (groups[k] = groups[k] || []).push(o); });

  // Out-of-scope tile: Update opens the camera directly (normal OK/Not-OK capture)
  const updateSite = (socName, t, l, s) => openCapture(socName, t, l, s, 'inspect');

  const siteTile = (socName, t, l, s) => {
    const active = scoped.has(s.id);
    if (!active) {
      // Prefer the client tapped in this round's capture so the tile updates instantly (even offline)
      const latestCamp = [...entriesFor(s.id)].reverse().find((e) => e.campaign_id)?.campaign_id;
      const occ = campMap[latestCamp || s.current_campaign_id];
      const today = new Date().toISOString().slice(0, 10);
      const expired = occ && occ.end_date && occ.end_date.slice(0, 10) < today;
      const dlabel = isPoster(s.position_type) ? `Poster · ${wallOf(s.position_type)} wall` : POSITIONS[s.position_type];
      return (
        <div key={s.id} data-testid={`iv-site-${s.id}`} className="flex items-center gap-3 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-3">
          {occ?.artwork_file_id
            ? <img src={fileUrl(occ.artwork_file_id)} alt="artwork" className="h-14 w-14 shrink-0 rounded-md border border-slate-300 object-cover" />
            : <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-md border border-slate-300 bg-slate-100"><Lock className="h-5 w-5 text-slate-400" /></div>}
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500">{t.name} · {l.name}</p>
            <p className="text-sm font-semibold text-slate-700">{dlabel}</p>
            <p className="text-xs text-slate-500">Current: {occ ? (occ.client_name || occ.name) : 'Empty'} {occ && <span className={`font-bold ${expired ? 'text-rose-600' : 'text-emerald-600'}`}>{expired ? '🔴 Expired' : '🟢 Active'}</span>}</p>
          </div>
          {canEdit && (() => {
            const es = entriesFor(s.id);
            const lastAct = es.length ? es[es.length - 1].action_type : null;
            if (!es.length) return <button data-testid={`iv-update-${s.id}`} onClick={() => updateSite(socName, t, l, s)} className="shrink-0 rounded-md border-2 border-primary px-3 py-1.5 text-xs font-bold text-primary transition hover:bg-primary/10">Update</button>;
            if (lastAct === 'damage') return (
              <div className="flex shrink-0 flex-col items-end gap-1">
                <span data-testid={`iv-notok-${s.id}`} className="flex items-center gap-1 rounded-md border-2 border-rose-500 bg-rose-100 px-3 py-1 text-[11px] font-black uppercase text-rose-800">Not OK</span>
                <button data-testid={`iv-repair-${s.id}`} onClick={() => openCapture(socName, t, l, s, 'repair')} className="flex items-center gap-1 rounded-md border-2 border-cyan-600 px-3 py-1.5 text-xs font-bold text-cyan-700 transition hover:bg-cyan-50"><Wrench className="h-3.5 w-3.5" /> Repair</button>
              </div>
            );
            if (lastAct === 'repair') return <span data-testid={`iv-repaired-${s.id}`} className="flex shrink-0 items-center gap-1 rounded-md border-2 border-emerald-500 bg-emerald-100 px-3 py-1.5 text-xs font-black uppercase text-emerald-800"><Wrench className="h-3.5 w-3.5" /> Repaired &amp; Updated</span>;
            return <span data-testid={`iv-updated-${s.id}`} className="flex shrink-0 items-center gap-1 rounded-md border-2 border-emerald-500 bg-emerald-100 px-3 py-1.5 text-xs font-black uppercase text-emerald-800"><Check className="h-3.5 w-3.5" /> Updated</span>;
          })()}
        </div>
      );
    }
    // Button no longer waits on per-site eligible-campaigns fetch (that caused "dead"/Loading buttons);
    // the inspection is already loaded here and openCapture sources chips from it.
    const loaded = true;
    const entries = entriesFor(s.id);
    const acts = entries.map((e) => e.action_type);
    const isInstall = isInstallSite(s.id);
    const occCampId = [...entries].reverse().find((e) => e.campaign_id)?.campaign_id || s.current_campaign_id;
    const occCamp = occCampId ? campMap[occCampId] : null;
    // Lead with the CLIENT name — it matches the picker chips the operator taps
    const occName = occCamp ? (occCamp.client_name || occCamp.name) : '';
    // Empty/unknown wall on an install tile -> skip the deinstall step, go straight to Install
    const stage = roundStage(isInstall, acts, isInstall && !acts.length && !occCampId);
    const StageIcon = stage.Icon;
    const ts = toneStyles(stage.kind);
    // Named two-step flow: Deinstall (old client) -> Install (new client); replace_map overrides the old-client default
    const newCamp = installTarget(s.id);
    const repOldCamp = (insp.replace_map || {})[newCamp?.id] ? campMap[(insp.replace_map || {})[newCamp?.id]] : null;
    const oldName = (repOldCamp && (repOldCamp.client_name || repOldCamp.name)) || occName || 'old board';
    const newName = (newCamp && (newCamp.client_name || newCamp.name)) || 'new board';
    const btnLabel = stage.kind === 'deinstall' ? `Deinstall (${oldName})`
      : stage.kind === 'install' ? `Install (${newName})`
      : stage.label;
    const stepHint = stage.kind === 'deinstall' ? 'Step 1 of 2 — remove old board'
      : (stage.kind === 'install' && acts.includes('deinstall') ? 'Step 2 of 2 — put up new board' : '');
    const label = isPoster(s.position_type) ? `Poster · ${wallOf(s.position_type)} wall` : POSITIONS[s.position_type];
    const tMeta = SITE_TYPE[s.position_type] || { label, cls: 'border-slate-500 bg-slate-800 text-slate-200', Icon: ImageIcon };
    const TypeIcon = tMeta.Icon;
    const liftIdx = (t.lifts || []).findIndex((x) => x.id === l.id);
    const hasSide = l.side === 'left' || l.side === 'right';
    const showLiftSides = (t.lift_layout || 'lr') === 'lr' && (hasSide || (liftIdx >= 0 && liftIdx < 2));
    const isLeftLift = hasSide ? l.side === 'left' : liftIdx === 0;
    // Lift badge = solid filled block (location grouping) with its side arrow; pill+chip marks the site wall
    const liftCls = !showLiftSides ? 'bg-slate-500 text-white'
      : isLeftLift ? 'bg-sky-600 text-white' : 'bg-rose-600 text-white';
    const LiftIcon = !showLiftSides ? Building2 : (isLeftLift ? ArrowLeft : ArrowRight);
    return (
      <div key={s.id} data-testid={`iv-site-${s.id}`} className={`flex overflow-hidden rounded-xl border-[3px] ${ts.tile}`}>
        <div className={`flex w-11 shrink-0 items-center justify-center border-r-2 py-2 ${ts.pillar}`}>
          <span className="text-lg font-black uppercase tracking-widest" style={{ writingMode: 'vertical-rl' }}>{t.name}</span>
        </div>
        <div className="min-w-0 flex-1 p-3">
          <div className={`-mx-3 -mt-3 mb-2 flex items-center gap-2 border-b-2 px-3 py-1.5 ${ts.pillar}`}>
            <span data-testid={`iv-towerhdr-${s.id}`} className="shrink-0 text-sm font-black uppercase tracking-widest">{t.name}</span>
            {occCamp && <span data-testid={`iv-camphdr-${s.id}`} className="min-w-0 truncate text-sm font-bold uppercase tracking-wide opacity-80">· {occName}{occCamp.name && occCamp.name !== occName ? <span className="font-medium normal-case opacity-70"> ({occCamp.name})</span> : null}</span>}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span data-testid={`iv-liftbadge-${s.id}`} className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-base font-black uppercase tracking-wide shadow-sm ${liftCls}`}>
              <LiftIcon className="h-5 w-5" /> {l.name}
            </span>
          </div>
          <div className="mt-2">
            <span data-testid={`iv-sitebadge-${s.id}`} className={`inline-flex items-center gap-2 rounded-full border-2 py-1 pl-1 pr-3.5 text-base font-extrabold uppercase tracking-wide ${tMeta.cls}`}>
              <span className={`flex h-7 w-7 items-center justify-center rounded-full ${tMeta.chip}`}><TypeIcon className="h-4 w-4" /></span> {tMeta.label}
            </span>
          </div>
          {entries.length > 0 && (
            <div className="mt-2.5 flex gap-2 overflow-x-auto pb-1">
              {entries.map((e, i) => (
                <button key={i} data-testid={`iv-thumb-${s.id}-${i}`} onClick={() => { setEditOpen(false); setLightbox({ entries, index: i, label, siteId: s.id }); }} className="shrink-0 text-center">
                  {e.url && <img src={e.url} alt={e.action_type} className="h-20 w-20 rounded-lg border-2 border-white/30 object-cover" />}
                  <span className={`mt-0.5 block text-[10px] font-bold uppercase ${ts.thumbLabel}`}>{ACTION_LABEL[e.action_type] || e.action_type}{e.source === 'queue' ? (e.status === 'failed' ? ' ⚠️' : e.status === 'synced' ? '' : ' ⏳') : ''}</span>
                </button>
              ))}
            </div>
          )}
          {canEdit && entries.some((e) => e.source === 'queue' && e.status === 'failed') && (
            <button data-testid={`iv-retry-${s.id}`} onClick={() => entries.filter((e) => e.source === 'queue' && e.status === 'failed').forEach((e) => retryItem(e.queueId))}
              className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg border-2 border-amber-500 bg-amber-50 px-3 py-2 text-xs font-bold uppercase text-amber-800 transition hover:bg-amber-100">
              ⚠️ Photo not synced — Retry now
            </button>
          )}
          {(entries.length > 0 || canEdit) && (
            <div className="mt-3 flex items-center gap-2">
              {entries.length > 0 && (
                <button data-testid={`iv-preview-${s.id}`} onClick={() => { setEditOpen(false); setLightbox({ entries, index: 0, label, siteId: s.id }); }} title="Preview photos" className={`flex h-14 items-center justify-center rounded-lg border-2 px-4 transition ${ts.outline}`}>
                  <Eye className="h-6 w-6" />
                </button>
              )}
              {canEdit && entries.length > 0 && (
                <button data-testid={`iv-delete-${s.id}`} disabled={busySite === s.id} onClick={() => deleteLast(s.id)} title="Delete last capture (step back)" className={`flex h-14 items-center justify-center rounded-lg border-2 px-4 transition ${ts.outline}`}>
                  <RotateCcw className="h-6 w-6" />
                </button>
              )}
              {canEdit && (
                <button data-testid={`iv-capture-${s.id}`} disabled={!loaded || stage.frozen || busySite === s.id} onClick={() => loaded && !stage.frozen && openCapture(socName, t, l, s, stage.kind)} className={`flex h-16 flex-1 items-center justify-center gap-2 rounded-lg text-lg font-black uppercase tracking-wider ${!loaded ? 'cursor-wait bg-secondary text-muted-foreground opacity-70' : (ts.btn || stage.cls)}`}>
                  {loaded ? <><StageIcon className="h-6 w-6 shrink-0" /> <span className="flex flex-col items-center leading-tight"><span className="line-clamp-2">{btnLabel}</span>{stepHint && <span className="text-[10px] font-semibold normal-case tracking-normal opacity-80">{stepHint}</span>}</span></> : <><Loader2 className="h-6 w-6 animate-spin" /> Loading…</>}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <InspectionBanner inspectionId={id} />
      <Breadcrumbs items={[{ label: 'Inspections', to: '/inspections' }, { label: headTitle }]} />
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="overline text-primary flex items-center gap-2"><ClipboardList className="h-3.5 w-3.5" /> {insp.mode === 'install' ? 'Installation Round' : 'Inspection Round'}{insp.scope === 'multi' ? ' · Multi-campaign' : ''}</p>
          <h1 className="font-head text-3xl font-bold">{headTitle}</h1>
          {insp.campaign_name && <p className="text-sm text-muted-foreground">Campaign: <span className="text-foreground">{insp.campaign_name}</span></p>}
        </div>
        <ReportBuilderDialog scope={{ inspection_id: id }} inspectionMode triggerLabel="Inspection Report" campaignChoices={campChoices} societyChoices={societies.map((s) => ({ id: s.id, name: s.name }))} defaults={{ title: headTitle, client_name: insp.campaign_name || '' }} />
      </div>

      <div className="flex gap-2" data-testid="iv-view-tabs">
        <button data-testid="iv-view-sites" onClick={() => setView('sites')} className={`rounded-lg border-2 px-4 py-2 text-sm font-bold transition ${view === 'sites' ? 'border-primary bg-primary text-primary-foreground' : 'border-border bg-card text-slate-600'}`}>All Sites</button>
        <button data-testid="iv-view-repairs" onClick={() => setView('repairs')} className={`rounded-lg border-2 px-4 py-2 text-sm font-bold transition ${view === 'repairs' ? 'border-rose-500 bg-rose-500 text-white' : 'border-border bg-card text-slate-600'}`}>Repairs ({repairSites.length})</button>
      </div>

      {isAdmin && (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="iv-groupby">
          <span className="mr-1 text-xs text-muted-foreground">Group by:</span>
          {[['society', 'Society'], ['client', 'Client'], ['site_type', 'Site type'], ['status', 'Status']].map(([k, lbl]) => (
            <button key={k} data-testid={`iv-groupby-${k}`} onClick={() => { setGroupBy(k); setSelSoc(null); }} className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${groupBy === k ? 'border-primary bg-primary/15 text-primary' : 'border-border text-slate-300 hover:text-white'}`}>{lbl}</button>
          ))}
        </div>
      )}

      {view === 'repairs' ? (
        <div className="space-y-4" data-testid="iv-repairs">
          {repairGroups.length ? repairGroups.map(([soc, arr]) => (
            <div key={soc} className="rounded-xl border border-border bg-card">
              <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                <Building2 className="h-5 w-5 text-primary" />
                <span className="font-head text-lg font-bold">{soc}</span>
                <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700">{arr.length} to repair</span>
              </div>
              <div className="space-y-2 p-3">
                {arr.map((o) => siteTile(o.soc.name, o.t, o.l, o.s))}
              </div>
            </div>
          )) : <p className="py-8 text-center text-sm text-muted-foreground">No pending repairs.</p>}
        </div>
      ) : isAdmin && groupBy !== 'society' ? (
        <div className="space-y-4">
          {Object.entries(groups).map(([k, arr]) => (
            <div key={k} data-testid={`iv-group-${k}`} className="rounded-xl border border-border bg-card">
              <div className="flex items-center gap-2 border-b border-border px-4 py-3">
                <span className="font-head text-lg font-bold">{k}</span>
                <span className="rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold text-primary">{arr.length} sites</span>
              </div>
              <div className="grid gap-2 p-3 sm:grid-cols-2 lg:grid-cols-3">
                {arr.map((o) => siteTile(o.soc.name, o.t, o.l, o.s))}
              </div>
            </div>
          ))}
          {!allSites.length && <p className="py-6 text-center text-sm text-muted-foreground">No sites in this inspection.</p>}
        </div>
      ) : !activeSoc ? (
        societyViews.length ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {societyViews.map((soc) => {
              const { total, done, issues } = socStats(soc);
              const pct = total ? Math.round((done / total) * 100) : 0;
              return (
                <button key={soc.id} data-testid={`iv-pick-society-${soc.id}`} onClick={() => setSelSoc(soc.id)} className="rounded-xl border border-border bg-card p-4 text-left transition hover:border-primary">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-primary" />
                    <span className="font-head text-lg font-bold">{soc.name}</span>
                    <ChevronRight className="ml-auto h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{total} sites{issues > 0 ? ` · ${issues} need action` : ''}</p>
                  <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                    <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="mt-1 text-[11px] font-medium text-emerald-300">{done}/{total} done</p>
                </button>
              );
            })}
          </div>
        ) : <p className="py-6 text-center text-sm text-muted-foreground">No sites are assigned to this inspection.</p>
      ) : (
        <div data-testid={`iv-society-${activeSoc.id}`} className="rounded-xl border border-border bg-card">
          <div className="sticky top-14 z-10 border-b border-border bg-card/95 backdrop-blur">
            <div className="flex items-center gap-2 px-4 py-3">
              {societyViews.length > 1 && (
                <button data-testid="iv-back-societies" onClick={() => setSelSoc(null)} className="mr-1 flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-muted-foreground hover:text-white">
                  <ChevronLeft className="h-4 w-4" /> Societies
                </button>
              )}
              <Building2 className="h-6 w-6 text-primary" />
              <span className="font-head text-2xl font-black uppercase tracking-wide">{activeSoc.name}</span>
              {activeStats && (
                <span data-testid="iv-society-progress" className="ml-auto flex items-center gap-2">
                  <span className="font-mono text-xl font-black text-emerald-700">{activeStats.done}/{activeStats.total}</span>
                  <span className="h-2.5 w-20 overflow-hidden rounded-full bg-slate-200">
                    <span className={`block h-full rounded-full transition-all ${activeStats.done === activeStats.total && activeStats.total > 0 ? 'bg-emerald-500' : activeStats.done > 0 ? 'bg-amber-400' : 'bg-slate-500'}`} style={{ width: `${activePct}%` }} />
                  </span>
                </span>
              )}
            </div>
            {activeSoc.visTowers.length > 1 && (
              <div className="flex gap-2 overflow-x-auto px-4 pb-3">
                {activeSoc.visTowers.map((t) => {
                  const tw = towerStats(t);
                  const tabCls = tw.done === tw.total && tw.total > 0
                    ? 'border-emerald-500 bg-emerald-600 text-white'
                    : tw.done > 0
                      ? 'border-amber-400 bg-amber-500 text-black'
                      : 'border-border bg-white text-slate-700';
                  return (
                    <button key={t.id} data-testid={`iv-towtab-${t.id}`} onClick={() => scrollToTower(t.id)} className={`shrink-0 rounded-lg border-2 px-4 py-2.5 text-sm font-black uppercase tracking-wide transition ${tabCls}`}>
                      {t.name} · {tw.done}/{tw.total}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="space-y-4 p-3 sm:p-4">
            {activeSoc.visTowers.map((t) => (
              <div key={t.id} id={`iv-towsec-${t.id}`} data-testid={`iv-tower-${t.id}`} className="scroll-mt-32 border-l-2 border-primary/40 pl-3 sm:pl-4">
                <p className="flex items-center gap-2 font-head text-xl font-black uppercase tracking-wide"><Layers className="h-5 w-5 text-primary" /> {t.name}</p>
                <div className="mt-2 space-y-3">
                  {t.lifts.map((l) => (
                    <div key={l.id} data-testid={`iv-lift-${l.id}`} className="border-l-2 border-border pl-3 sm:pl-4">
                      <p className="text-sm font-semibold text-muted-foreground">{l.name}</p>
                      <div className="mt-1.5 space-y-1.5">
                        {[...l.sites].sort((a, b) => POS_ORDER.indexOf(a.position_type) - POS_ORDER.indexOf(b.position_type)).map((s) => siteTile(activeSoc.name, t, l, s))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {lightbox && (
        <Dialog open onOpenChange={(v) => !v && setLightbox(null)}>
          <DialogContent className="max-w-lg gap-0 overflow-hidden border-border bg-white p-0" data-testid="iv-lightbox">
            <DialogTitle className="shrink-0 px-4 pt-4 font-head text-base text-slate-900">{lightbox.label} · {ACTION_LABEL[lightbox.entries[lightbox.index].action_type] || lightbox.entries[lightbox.index].action_type}</DialogTitle>
            <div className="relative mt-3 bg-black">
              <img src={lightbox.entries[lightbox.index].url} alt="preview" className="max-h-[72vh] w-full object-contain" />
              {lightbox.entries.length > 1 && (
                <>
                  <button data-testid="iv-lightbox-prev" onClick={() => { setEditOpen(false); setLightbox((lb) => ({ ...lb, index: (lb.index - 1 + lb.entries.length) % lb.entries.length })); }} className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white transition hover:bg-black/80"><ChevronLeft className="h-5 w-5" /></button>
                  <button data-testid="iv-lightbox-next" onClick={() => { setEditOpen(false); setLightbox((lb) => ({ ...lb, index: (lb.index + 1) % lb.entries.length })); }} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white transition hover:bg-black/80"><ChevronRight className="h-5 w-5" /></button>
                </>
              )}
            </div>
            {lightbox.entries.length > 1 && (
              <div className="flex items-center justify-center gap-1.5 p-3">
                {lightbox.entries.map((e, i) => (
                  <span key={i} className={`h-1.5 rounded-full transition-all ${i === lightbox.index ? 'w-5 bg-primary' : 'w-1.5 bg-slate-300'}`} />
                ))}
              </div>
            )}
            {isAdmin && lightbox.entries[lightbox.index].source === 'server' && (
              <div className="border-t border-border p-4" data-testid="iv-edit-panel">
                {!editOpen ? (
                  <button data-testid="iv-edit-open" onClick={() => openEdit(lightbox.entries[lightbox.index])} className="flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-medium text-slate-700 transition hover:bg-slate-100">
                    <Pencil className="h-4 w-4" /> Edit photo details
                  </button>
                ) : (
                  <div className="space-y-3">
                    <div>
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Client / campaign</p>
                      <div className="flex flex-wrap gap-1.5">
                        {(eligMap[lightbox.siteId] || []).map((c) => (
                          <button key={c.id} data-testid={`iv-edit-client-${c.id}`} onClick={() => setEditForm((f) => ({ ...f, campaign_id: c.id }))}
                            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${editForm.campaign_id === c.id ? 'border-primary bg-primary text-primary-foreground' : 'border-slate-400 text-slate-700'}`}>
                            {c.client_name || c.name}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Status / result</p>
                      <select data-testid="iv-edit-status" value={editForm.new_status} onChange={(e) => setEditForm((f) => ({ ...f, new_status: e.target.value }))}
                        className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm text-slate-900">
                        {EDIT_STATUSES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                      </select>
                    </div>
                    <div>
                      <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">Notes</p>
                      <input data-testid="iv-edit-notes" value={editForm.notes} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                        className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm text-slate-900" placeholder="Optional note" />
                    </div>
                    <div className="flex gap-2">
                      <button data-testid="iv-edit-cancel" onClick={() => setEditOpen(false)} className="flex-1 rounded-md border border-border px-3 py-2 text-sm text-slate-600">Cancel</button>
                      <button data-testid="iv-edit-save" disabled={savingEdit} onClick={saveProofEdit} className="flex-1 rounded-md bg-emerald-600 px-3 py-2 text-sm font-semibold text-white transition hover:bg-emerald-500 disabled:opacity-50">Save changes</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}

      <CameraCapture
        open={!!cam}
        title={cam?.title || 'Capture'}
        stampLines={cam?.stampLines || []}
        campaignOptions={cam?.campaignOptions || []}
        initialCampaignId={cam?.initialCampaignId || ''}
        expectedCampaignId={cam?.expectedCampaignId || ''}
        expectedClientName={cam?.expectedClientName || ''}
        requireCampaign={cam?.requireCampaign || false}
        results={cam?.results || null}
        onClose={() => setCam(null)}
        onCapture={onCapture}
      />
    </div>
  );
}
